import type React from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import SiteRoutes from "./routes/site.routes";
import AppRoutes from "./routes/app.routes";
import PrivateRoute from "@/components/common/PrivateRoute/PrivateRoute";
import NotFoundPage from "./pages/common/NotFoundPage/NotFoundPage";
import { useEffect, useCallback, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/libs/supabase";
import { convertSupabaseUser } from "@/utils/userUtils";
import { fetchUserProfile } from "@/services/userService";
import LoadingScreen from "@/components/common/LoadingScreen/LoadingScreen";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { ToastProvider } from "@/providers/ToastProvider";

const AppContent: React.FC = () => {
	const { updateAuthState, loading } = useAuth();
	const [authChecked, setAuthChecked] = useState(false);

	const checkAuth = useCallback(async () => {
		if (loading || authChecked) return;

		const { data, error } = await supabase.auth.getSession();
		if (error) {
		} else if (data?.session) {
			try {
				const userProfile = await fetchUserProfile(data.session.user.id);
				if (userProfile) {
					updateAuthState(userProfile, true);
				}
			} catch (error) {
				console.error("ユーザープロフィール取得エラー:", error);
			}
		} else {
			console.log("セッションなし");
		}
		setAuthChecked(true);
	}, [loading, authChecked, updateAuthState]);

	useEffect(() => {
		checkAuth();
	}, [checkAuth]);

	useEffect(() => {
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange(async (event, session) => {
			if (event === "SIGNED_IN" && session) {
				const appUser = convertSupabaseUser(session.user);
				updateAuthState(appUser, true);
			} else if (event === "SIGNED_OUT") {
				updateAuthState(null, false);
			}
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [updateAuthState]);

	if (loading || !authChecked) {
		return <LoadingScreen message="認証チェック中..." />;
	}

	return (
		<Routes>
			{/* サイト全体のルート */}
			<Route path="/*" element={<SiteRoutes />} />

			{/* アプリケーション（認証が必要な）ルート */}
			<Route path="/webapp" element={<PrivateRoute />}>
				<Route path="*" element={<AppRoutes />} />
			</Route>

			{/* Not Found ページ */}
			<Route path="*" element={<NotFoundPage />} />
		</Routes>
	);
};

const App: React.FC = () => {
	return (
		<ThemeProvider>
			<ToastProvider>
				<Router>
					<AppContent />
				</Router>
			</ToastProvider>
		</ThemeProvider>
	);
};

export default App;
